import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import "firebase/firestore";
import { Invoice } from "../interfaces/invoice";
import { checkInvoices } from "../utils/models/checkers/checkInvoices";
import { useSelector } from "react-redux";
import { authSelector } from "../../domains/auth/authSlice";
import { FirestoreInvoicesContext } from "./FirestoreContext";
import { useFirebase, useFirestore } from "react-redux-firebase";
import { formatDateOnRead } from "../utils/time/formatTimezoneOffset";
import { inventoryQueries } from "../services/firestore/queries/inventoryQueries";
import { sortBy } from "../utils/sorters/sortBy";
import { emailSettingsQuery } from "../services/firestore/queries/orgQueries";
import { venueQueries } from "../services/firestore/queries/venueQueries";
import { checkInventory } from "../utils/models/checkers/checkInventory";
import { InventoryItem } from "../interfaces/inventoryItem";
import { CheckTaxRates } from "../utils/models/checkers/checkTaxRates";
import Nifty from "../utils/Nifty";
import { TaxRate } from "../interfaces/taxRate";
import { CheckFile } from "../../domains/file-manager/file";
import { checkVenue } from "../utils/models/checkers/checkVenue";

export function FirestoreProvider({ children }: { children: React.ReactNode }) {
  const firestore = useFirestore();
  const firebase = useFirebase();
  const { orgData, userData, fsOrgPrefix } = useSelector(authSelector);

  const [loadingInventory, setLoadingInventory] = useState(false);

  const [users, setUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [tags, setTags] = useState<any[]>([]);
  const [taxes, setTaxes] = useState<any[]>([]);
  const [files, setFiles] = useState<any[]>([]);
  const [loadingVenues, setLoadingVenues] = useState(false);
  const [invoiceTextData, setInvoiceTextData] = useState<any>(null);
  const [emailInvoiceSettings, setEmailInvoiceSettings] = useState<any>(null);
  const [emailEstimateSettings, setEmailEstimateSettings] = useState<any>(null);
  const [venues, setVenues] = useState<any[]>([]);
  const [inventory, setInventory] = useState<any[]>([]);
  const [initialInventoryLoaded, setInitialInventoryLoaded] = useState(false);
  const [lastInventoryProcessedTime, setLastInventoryProcessedTime] = useState(0);
  const [totalInventoryCount, setTotalInventoryCount] = useState(0);
  const [categories, setCategories] = useState<any[]>([]);

  const isoToday = useCallback(() => {
    const formattedDate = formatDateOnRead({
      date: new Date(),
      orgTimezone: orgData.orgTimezone,
    })?.toDate();

    const year = formattedDate?.getFullYear();
    const month = ((formattedDate?.getMonth() ?? 0) + 1).toString().padStart(2, "0");
    const day = formattedDate?.getDate().toString().padStart(2, "0");
    const mobileFriendly = `${year}-${month}-${day}`;
    return mobileFriendly;
  }, [orgData]);

  const [invoicesData, setInvoicesData] = useState<{
    todaysInvoices: Invoice[];
    yesterdayInvoices: Invoice[];
    tomorrowInvoices: Invoice[];
    lastWeekInvoices: Invoice[];
    nextWeekInvoices: Invoice[];
  }>({
    todaysInvoices: [],
    yesterdayInvoices: [],
    tomorrowInvoices: [],
    lastWeekInvoices: [],
    nextWeekInvoices: [],
  });

  const [invoices, setInvoices] = useState<Invoice[]>([]);

  // MARK: Load Inventory - Initial Load
  const loadInventory = useCallback(async () => {
    if (!orgData) return;

    try {
      setLoadingInventory(true);
      // Get initial data with a regular query
      const query = await firestore.collection(`orgs/${orgData.id}/inventory`).orderBy("name").where("isActive", "==", true).get();

      const items = query.docs.map((doc) => checkInventory(doc));
      setInventory(sortBy(items, "name"));
      setLastInventoryProcessedTime(Date.now());
      setTotalInventoryCount(query.size);
      setInitialInventoryLoaded(true);

      console.log("totalInventoryCount", query.size);
      console.log("lastInventoryProcessedTime", Date.now());
    } catch (error) {
      console.error("Error loading inventory:", error);
    } finally {
      setLoadingInventory(false);
    }
  }, [firestore, orgData]);

  // MARK: Load Users
  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const usersQuery = await firestore.collection("orgs").doc(orgData.id).collection("users").where("permissions", "!=", "warehouse").get();

      const users = usersQuery.docs
        .map((snap) => snap.data())
        .map((data) => ({
          ...data,
          name: `${data.firstName} ${data.lastName}`,
          id: data.id,
        }));

      setUsers(sortBy(users, "name"));
    } catch (error) {
      console.error("Error loading users:", error);
    } finally {
      setLoadingUsers(false);
    }
  }, [firestore, orgData]);

  // MARK: Load Venues
  const loadVenues = useCallback(async () => {
    setLoadingVenues(true);
    try {
      if (fsOrgPrefix) {
        const queries = venueQueries({ firestore }, fsOrgPrefix);
        if (queries) {
          const res = await queries.fetchActiveVenues();
          setVenues(res);
        }
      }
    } catch (error) {
      console.error("Error loading venues:", error);
    } finally {
      setLoadingVenues(false);
    }
  }, [firestore, fsOrgPrefix]);

  // MARK: Load Tags
  const loadTags = useCallback(async () => {
    if (!orgData) return;
    try {
      const snapshot = await firestore.collection(`orgs/${orgData.id}/tags`).get();
      const fetchedTags = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTags(fetchedTags);
    } catch (error) {
      console.error("Error loading tags:", error);
    }
  }, [firestore, orgData]);

  // MARK: Load Categories
  const loadCategories = useCallback(async () => {
    if (!orgData) return;
    const snapshot = await firestore.collection(`orgs/${orgData.id}/categories`).get();
    const fetchedCategories = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setCategories(fetchedCategories);
  }, [firestore, orgData]);

  const loadFiles = useCallback(async () => {
    if (!orgData) return;
    const querySnapshot = await firestore.collection("orgs").doc(orgData.id).collection("fileManager").get();
    const files = querySnapshot.docs.map((doc) => CheckFile(doc));
    setFiles(files);
  }, [firestore, orgData]);

  // MARK: Load Taxes
  const loadTaxes = useCallback(async () => {
    if (!orgData) return;
    try {
      const snapshot = await firestore.collection(`orgs/${orgData.id}/taxCodes`).where("isActive", "==", true).get();
      const rates = snapshot.docs.map((s) => CheckTaxRates(s));
      const sorted: TaxRate[] = Nifty.SortByString(rates, "name", "ascending ");

      setTaxes(sorted);
    } catch (error) {
      console.error("Error loading tags:", error);
    }
  }, [firestore, orgData]);

  // MARK: Invoice Text
  const fetchInvoiceTextCustomization = useCallback(async () => {
    try {
      const textSnap = await firestore.collection("orgs").doc(orgData.id).collection("textCustomization").doc("invoice").get();

      if (textSnap.exists) {
        const textData = textSnap.data();
        setInvoiceTextData(textData);
      }
    } catch (error) {
      console.error("Error fetching invoice text customization:", error);
    }
  }, [firestore, orgData]);

  // MARK: Email Settings
  const fetchEmailSettings = useCallback(async () => {
    try {
      const invoiceSettings = await emailSettingsQuery({ firestore }, fsOrgPrefix).fetchEmailSettings(orgData.id, "invoiceEmailSettings");
      if (invoiceSettings) {
        setEmailInvoiceSettings(invoiceSettings);
      }

      const estimateSettings = await emailSettingsQuery({ firestore }, fsOrgPrefix).fetchEmailSettings(orgData.id, "estimateEmailSettings");
      if (estimateSettings) {
        setEmailEstimateSettings(estimateSettings);
      }
    } catch (error) {
      console.error("Error fetching email settings:", error);
    }
  }, [firestore, fsOrgPrefix, orgData]);

  const addInventoryItem = useCallback((newItem: InventoryItem) => {
    setInventory((prevInventory) => {
      const exists = prevInventory.some((item) => item.id === newItem.id);
      if (exists) {
        return prevInventory.map((item) => (item.id === newItem.id ? newItem : item));
      }
      return [...prevInventory, newItem];
    });
  }, []);

  const addUser = useCallback((newUser: any) => {
    setUsers((prevUsers) => {
      const updatedUsers = [...prevUsers, newUser];
      return sortBy(updatedUsers, "name");
    });
  }, []);

  const addTag = useCallback((newTag: any) => {
    setTags((prevTags) => {
      const updatedTags = [...prevTags, newTag];
      return updatedTags;
    });
  }, []);

  const addCategory = useCallback((newCategory: any) => {
    setCategories((prevCategories) => {
      const updatedCategories = [...prevCategories, newCategory];
      return sortBy(updatedCategories, "name");
    });
  }, []);

  const addVenue = useCallback((newVenue: any) => {
    setVenues((prevVenues) => {
      const updatedVenues = [...prevVenues, newVenue];
      return sortBy(updatedVenues, "venueName");
    });
  }, []);

  const addTax = useCallback((newTax: TaxRate) => {
    setTaxes((prevTaxes) => {
      const updatedTaxes = [...prevTaxes, newTax];
      return Nifty.SortByString(updatedTaxes, "name", "ascending ");
    });
  }, []);

  const addFile = useCallback((newFile: any) => {
    setFiles((prevFiles) => {
      const updatedFiles = [...prevFiles, newFile];
      return updatedFiles;
    });
  }, []);

  const addInvoice = useCallback((newInvoice: Invoice, listType: keyof typeof invoicesData) => {
    setInvoicesData((prevData) => ({
      ...prevData,
      [listType]: [...prevData[listType], newInvoice],
    }));
  }, []);

  const updateInventoryItem = useCallback((id: string, updatedItem: Partial<InventoryItem>) => {
    setInventory((prevInventory) => prevInventory.map((item) => (item.id === id ? { ...item, ...updatedItem } : item)));
  }, []);

  const updateUser = useCallback((id: string, updatedUser: Partial<any>) => {
    setUsers((prevUsers) => prevUsers.map((user) => (user.id === id ? { ...user, ...updatedUser } : user)));
  }, []);

  const updateTag = useCallback((id: string, updatedTag: Partial<any>) => {
    setTags((prevTags) => prevTags.map((tag) => (tag.id === id ? { ...tag, ...updatedTag } : tag)));
  }, []);

  const updateCategory = useCallback((id: string, updatedCategory: Partial<any>) => {
    setCategories((prevCategories) => prevCategories.map((category) => (category.id === id ? { ...category, ...updatedCategory } : category)));
  }, []);

  const updateVenue = useCallback((id: string, updatedVenue: Partial<any>) => {
    setVenues((prevVenues) => prevVenues.map((venue) => (venue.id === id ? { ...venue, ...updatedVenue } : venue)));
  }, []);

  const updateTax = useCallback((id: string, updatedTax: Partial<TaxRate>) => {
    setTaxes((prevTaxes) => prevTaxes.map((tax) => (tax.id === id ? { ...tax, ...updatedTax } : tax)));
  }, []);

  const updateFile = useCallback((id: string, updatedFile: Partial<any>) => {
    setFiles((prevFiles) => prevFiles.map((file) => (file.id === id ? { ...file, ...updatedFile } : file)));
  }, []);

  const updateInvoice = useCallback((id: string, updatedInvoice: Partial<Invoice>, listType: keyof typeof invoicesData) => {
    setInvoicesData((prevData) => ({
      ...prevData,
      [listType]: prevData[listType].map((invoice) => (invoice.id === id ? { ...invoice, ...updatedInvoice } : invoice)),
    }));
  }, []);

  const updateInvoiceTextData = useCallback((data: any) => {
    setInvoiceTextData(data);
  }, []);

  const updateEmailInvoiceSettings = useCallback((settings: any) => {
    setEmailInvoiceSettings(settings);
  }, []);

  const updateEmailEstimateSettings = useCallback((settings: any) => {
    setEmailEstimateSettings(settings);
  }, []);

  useEffect(() => {
    if (!orgData) return;

    if (!initialInventoryLoaded && !loadingInventory) {
      console.log("Loading inventory...");
      loadInventory();
    }

    // // Set up real-time snapshot listeners for various collections
    let unsubscribeInventory: () => void = () => {};

    // // Real-time inventory listener - Only listen for future changes
    if (orgData && initialInventoryLoaded) {
      // Convert JavaScript timestamp to Firestore timestamp
      const firestoreTimestamp = new Date(lastInventoryProcessedTime);

      unsubscribeInventory = firestore
        .collection(`orgs/${orgData.id}/inventory`)
        .where("isActive", "==", true)
        .where("updatedOn", ">", firestoreTimestamp)
        .onSnapshot((snapshot) => {
          // Process only changes after initial load
          snapshot.docChanges().forEach((change) => {
            const item = checkInventory(change.doc);
            console.log("item", item);

            if (change.type === "added") {
              console.log("added", item);
              addInventoryItem(item);
            }
            if (change.type === "modified") {
              console.log("modified", item);
              updateInventoryItem(item.id, item);
            }
            if (change.type === "removed") {
              console.log("removed", item);
              setInventory((prev) => prev.filter((i) => i.id !== item.id));
            }
          });
        });
    }

    // Clean up subscriptions on unmount
    return () => {
      unsubscribeInventory();
    };
  }, [
    firestore,
    orgData,
    fsOrgPrefix,
    initialInventoryLoaded,
    loadInventory,
    fetchInvoiceTextCustomization,
    fetchEmailSettings,
    loadingInventory,
    lastInventoryProcessedTime,
    addInventoryItem,
    updateInventoryItem,
  ]);

  useEffect(() => {
    if (!orgData) return;

    // Set up real-time snapshot listeners for various collections
    let unsubscribeTags: () => void = () => {};
    let unsubscribeCategories: () => void = () => {};
    let unsubscribeFiles: () => void = () => {};
    let unsubscribeTaxes: () => void = () => {};
    let unsubscribeVenues: () => void = () => {};

    if (orgData) {
      // Real-time tags listener
      unsubscribeTags = firestore.collection(`orgs/${orgData.id}/tags`).onSnapshot((snapshot) => {
        snapshot.docChanges().forEach((change) => {
          const tag = {
            id: change.doc.id,
            ...change.doc.data(),
          };

          if (change.type === "added") {
            setTags((prev) => {
              if (prev.some((t) => t.id === tag.id)) return prev;
              return [...prev, tag];
            });
          }
          if (change.type === "modified") {
            setTags((prev) => prev.map((t) => (t.id === tag.id ? tag : t)));
          }
          if (change.type === "removed") {
            setTags((prev) => prev.filter((t) => t.id !== tag.id));
          }
        });
      });

      // Real-time categories listener
      unsubscribeCategories = firestore.collection(`orgs/${orgData.id}/categories`).onSnapshot((snapshot) => {
        snapshot.docChanges().forEach((change) => {
          const category = {
            id: change.doc.id,
            ...change.doc.data(),
          };

          if (change.type === "added") {
            setCategories((prev) => {
              if (prev.some((c) => c.id === category.id)) return prev;
              const updated = [...prev, category];
              return sortBy(updated, "name");
            });
          }
          if (change.type === "modified") {
            setCategories((prev) => {
              const updated = prev.map((c) => (c.id === category.id ? category : c));
              return sortBy(updated, "name");
            });
          }
          if (change.type === "removed") {
            setCategories((prev) => prev.filter((c) => c.id !== category.id));
          }
        });
      });

      // Real-time files listener
      unsubscribeFiles = firestore.collection(`orgs/${orgData.id}/fileManager`).onSnapshot((snapshot) => {
        snapshot.docChanges().forEach((change) => {
          const file = CheckFile(change.doc);

          if (change.type === "added") {
            setFiles((prev) => {
              if (prev.some((f) => f.id === file.id)) return prev;
              return [...prev, file];
            });
          }
          if (change.type === "modified") {
            setFiles((prev) => prev.map((f) => (f.id === file.id ? file : f)));
          }
          if (change.type === "removed") {
            setFiles((prev) => prev.filter((f) => f.id !== file.id));
          }
        });
      });

      // Real-time taxes listener
      unsubscribeTaxes = firestore
        .collection(`orgs/${orgData.id}/taxCodes`)
        .where("isActive", "==", true)
        .onSnapshot((snapshot) => {
          snapshot.docChanges().forEach((change) => {
            const tax = CheckTaxRates(change.doc);

            if (change.type === "added") {
              setTaxes((prev) => {
                if (prev.some((t) => t.id === tax.id)) return prev;
                const updated = [...prev, tax];
                return Nifty.SortByString(updated, "name", "ascending ");
              });
            }
            if (change.type === "modified") {
              setTaxes((prev) => {
                const updated = prev.map((t) => (t.id === tax.id ? tax : t));
                return Nifty.SortByString(updated, "name", "ascending ");
              });
            }
            if (change.type === "removed") {
              setTaxes((prev) => prev.filter((t) => t.id !== tax.id));
            }
          });
        });

      // Real-time venues listener
      if (fsOrgPrefix) {
        unsubscribeVenues = firestore
          .collection(`${fsOrgPrefix}venues`)
          .where("isActive", "==", true)
          .onSnapshot((snapshot) => {
            snapshot.docChanges().forEach((change) => {
              const venue = checkVenue(change.doc);

              if (venue && change.type === "added") {
                setVenues((prev) => {
                  if (prev.some((v) => v.id === venue.id)) return prev;
                  const updated = [...prev, venue];
                  return sortBy(updated, "venueName");
                });
              }
              if (venue && change.type === "modified") {
                setVenues((prev) => {
                  const updated = prev.map((v) => (v.id === venue.id ? venue : v));
                  return sortBy(updated, "venueName");
                });
              }
              if (venue && change.type === "removed") {
                setVenues((prev) => prev.filter((v) => v.id !== venue.id));
              }
            });
          });
      }

      // We still need to load users separately
      loadUsers();
      fetchInvoiceTextCustomization();
      fetchEmailSettings();
    }

    // Clean up subscriptions on unmount
    return () => {
      unsubscribeTags();
      unsubscribeCategories();
      unsubscribeFiles();
      unsubscribeTaxes();
      unsubscribeVenues();
    };
  }, [firestore, orgData, fsOrgPrefix, loadInventory, loadUsers, fetchInvoiceTextCustomization, fetchEmailSettings]);

  return (
    <FirestoreInvoicesContext.Provider
      value={{
        invoicesData,
        inventory,
        loadingInventory,
        initialInventoryLoaded,
        users,
        tags,
        categories,
        venues,
        taxes,
        files,
        invoiceTextData,
        emailInvoiceSettings,
        emailEstimateSettings,
        addInventoryItem,
        addUser,
        addTag,
        addCategory,
        addVenue,
        addTax,
        addFile,
        addInvoice,
        updateInventoryItem,
        updateUser,
        updateTag,
        updateCategory,
        updateVenue,
        updateTax,
        updateFile,
        updateInvoice,
        updateInvoiceTextData,
        updateEmailInvoiceSettings,
        updateEmailEstimateSettings,
      }}
    >
      {children}
    </FirestoreInvoicesContext.Provider>
  );
}

/*
 let unsubscribeInvoices: () => void;

    if (orgData) {

    const todayDateISO = isoToday();
    const todayDate = new Date(todayDateISO);

    // Calculate the date ranges for yesterday, tomorrow, last week, and next week
    const yesterday = new Date(todayDate);
    yesterday.setDate(yesterday.getDate() - 1);
    const tomorrow = new Date(todayDate);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const lastWeek = new Date(todayDate);
    lastWeek.setDate(lastWeek.getDate() - 7);
    const nextWeek = new Date(todayDate);
    nextWeek.setDate(nextWeek.getDate() + 7);

    unsubscribeInvoices = firestore
      .collection("orgs")
      .doc(orgData.id)
      .collection("invoices")
      .where("rentalDateStart", ">", lastWeek)
      .onSnapshot((snapshot) => {

        const allInvoices: Invoice[] = [];
        const invoicesToday: Invoice[] = [];
        const invoicesYesterday: Invoice[] = [];
        const invoicesTomorrow: Invoice[] = [];
        const invoicesLastWeek: Invoice[] = [];
        const invoicesNextWeek: Invoice[] = [];

        snapshot.docs.forEach((doc) => {
          const i = checkInvoices(doc, orgData.orgTimezone) as Invoice;
          allInvoices.push(i);

          if (i.mobileStartDate === todayDateISO || i.mobileEndDate === todayDateISO) {
            invoicesToday.push(i);
          }

          // Check if rentalStartDate or rentalEndDate falls within the desired ranges
          if (
            (i.rentalDateStart.toDate() >= yesterday && i.rentalDateStart.toDate() <= tomorrow) ||
            (i.rentalDateEnd.toDate() >= yesterday && i.rentalDateEnd.toDate() <= tomorrow)
          ) {
            invoicesYesterday.push(i);
          }

          if (
            (i.rentalDateStart.toDate() >= yesterday && i.rentalDateStart.toDate() <= tomorrow) ||
            (i.rentalDateEnd.toDate() >= yesterday && i.rentalDateEnd.toDate() <= tomorrow)
          ) {
            invoicesTomorrow.push(i);
          }

          if (
            (i.rentalDateStart.toDate() >= lastWeek && i.rentalDateStart.toDate() <= todayDate) ||
            (i.rentalDateEnd.toDate() >= lastWeek && i.rentalDateEnd.toDate() <= todayDate)
          ) {
            invoicesLastWeek.push(i);
          }

          if (
            (i.rentalDateStart.toDate() >= todayDate && i.rentalDateStart.toDate() <= nextWeek) ||
            (i.rentalDateEnd.toDate() >= todayDate && i.rentalDateEnd.toDate() <= nextWeek)
          ) {
            invoicesNextWeek.push(i);
          }
        });

        setInvoices(allInvoices);

        setInvoicesData((prevData) => ({
          ...prevData,
          todaysInvoices: invoicesToday,
          yesterdayInvoices: invoicesYesterday,
          tomorrowInvoices: invoicesTomorrow,
          lastWeekInvoices: invoicesLastWeek,
          nextWeekInvoices: invoicesNextWeek,
        }));
      });
    }
  
    // Clean up the listener when the component unmounts
    return () => {
      if (unsubscribeInvoices) unsubscribeInvoices();
    };
    */
